import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { useCompanyDefaultsAccess } from "../../Settings/components/common/company-defaults-access.ts"
import {
  type CommunicationRights,
  useCommunicationRights
} from "../communication-permissions.ts"

export interface HubRights extends CommunicationRights {
  readsMessages: boolean
  readsCampaigns: boolean
  automationsView: boolean
  automationsManage: boolean
  templatesView: boolean
  templatesCreate: boolean
  templatesEdit: boolean
  templatesDelete: boolean
  preferencesView: boolean
}

interface AutomationCodes {
  settings_automation_view?: boolean
  settings_automation_manage?: boolean
}

// The automation endpoints answer either the Settings automation rights or
// communication_settings_manage, and 404 while the lead_automation flag is off.
export const automationRightsOf = (
  rights: Pick<CommunicationRights, "settings">,
  codes: AutomationCodes | undefined,
  featureLeadAutomation: boolean | undefined
) => {
  const flag = featureLeadAutomation === true
  const manage =
    flag && (rights.settings || !!codes?.settings_automation_manage)
  return {
    automationsManage: manage,
    automationsView: manage || (flag && !!codes?.settings_automation_view)
  }
}

export const useHubRights = (): HubRights => {
  const rights = useCommunicationRights()
  const automationCodes = useAppSelector(
    (state) => state.auth?.permissions?.codes?.settings?.automations
  ) as AutomationCodes | undefined
  const featureLeadAutomation = useAppSelector(
    (state) => state.auth?.userInfo?.feature_lead_automation
  )
  const { can } = useCompanyDefaultsAccess()

  const templateAction = (action: "view" | "create" | "edit" | "delete") =>
    rights.templates || can("email_templates", action)

  return {
    ...rights,
    readsMessages: rights.send || rights.viewAll,
    readsCampaigns: rights.bulkSend || rights.viewAll,
    ...automationRightsOf(rights, automationCodes, featureLeadAutomation),
    // Matches the tab's route gate; a Company Defaults grant alone keeps the Settings page.
    templatesView: rights.templatesUse,
    templatesCreate: templateAction("create"),
    templatesEdit: templateAction("edit"),
    templatesDelete: templateAction("delete"),
    preferencesView: rights.viewAll
  }
}
